import React, { useEffect, useMemo, useState } from "react";
import {
    Avatar,
    Box,
    Button,
    Tabs,
    Tab,
    CardContent,
    CardHeader,
    Chip,
    Grid2,
    IconButton,
    LinearProgress,
    Link as MUILink,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
    FormControl,
    Select,
} from "@mui/material";
import FileRow from "../FileRow";
import Badge from '../Badge';
import { AutoCompleteComponent } from '@syncfusion/ej2-react-dropdowns';

import MoreVertIcon from "@mui/icons-material/MoreVert";
import FlagIcon from "@mui/icons-material/Flag";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CommentIcon from "@mui/icons-material/Comment";
import HistoryIcon from "@mui/icons-material/History";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import DescriptionIcon from '@mui/icons-material/Description';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import OutputIcon from '@mui/icons-material/Output';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';

const DEFAULT_ICON_SIZE = 12;

const stateMeta = {
    0: { label: "Pending", color: "warning" },
    1: { label: "In Progress", color: "primary" },
    2: { label: "Completed", color: "success" },
    3: { label: "Cancelled", color: "error" },
};

const priorityMeta = {
    0: { label: "Low", icon: <FlagIcon fontSize="small" /> },
    1: { label: "Medium", icon: <FlagIcon fontSize="small" /> },
    2: { label: "Hight", icon: <FlagIcon fontSize="small" /> },
    3: { label: "Urgent", icon: <FlagIcon fontSize="small" /> },
};

const logTypesMeta = {
    0: { label: "Info", color: "info" },
    1: { label: "Comment", color: "warning" },
    2: { label: "Status Change", color: "primary" },
    3: { label: "Priority Change", color: "info" },
    4: { label: "Assignment Change", color: "info" },
}

class WorkflowDetailDataType {
    static Text = 0;
    static Json = 1;
    static Document = 2;
    static DocumentList = 3;
}

class WORKFLOW_LOG_DATA_TYPE {
    static Text = 0;
    static Json = 1;
}

const WORKFLOW_STATUS = [
    'UNDEFINED',
    'RUNNING',
    'COMPLETED',
    'FAILED',
    'SUSPENDED'
];

const WORKFLOW_STATUS_COLOR = [
    'default',
    'primary',
    'success',
    'error',
    'warning'
];

const WORKFLOW_LOG_TYPES = [
    'INFO',
    'WARNING',
    'ERROR',
    'SUCCESS',
    'DATA',
    'DOCUMENT',
    'MAPPING (in)',
    'MAPPING (out)',
];

const WORKFLOW_LOG_TYPE_COLOR = [
    'info',
    'warning',
    'error',
    'success',
    'secondary',
    'default',
    'secondary',
    'secondary',
];


const WORKFLOW_STATUS_ICON = [
    <HelpOutlineIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <ScheduleIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <CheckCircleOutlineIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <ErrorOutlineIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <ScheduleIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />
];

const WORKFLOW_LOG_TYPE_ICON = [
    <ErrorOutlineIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <ScheduleIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <ErrorOutlineIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <CheckCircleOutlineIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <FormatAlignCenterIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <DescriptionIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <ExitToAppIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
    <OutputIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />,
];

const formatLog = (log, dataType) => {
    if (dataType === WORKFLOW_LOG_DATA_TYPE.Json) {
        try {
            const obj = JSON.parse(log);
            const res = JSON.stringify(obj, null, 4);
            return res;
        } catch (e) {
            console.log('Error parsing JSON log:', e);
            return log;
        }
    }
    return log;
}

function formatDate(iso, time = false) {
    if (!iso) return "—";
    const d = new Date(iso + 'Z');
    if (isNaN(d.getTime())) return iso || "—";
    return d.toLocaleString('en-US', time ? { hour: '2-digit', minute: '2-digit' } : { year: 'numeric', month: '2-digit', day: '2-digit' });
}


const formatDateLog = (dateString, utc = false) => {
    const date = utc ? new Date(dateString) : new Date(new Date(dateString + 'Z').toLocaleString('en-US'));
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
    const day = date.getDate().toString().padStart(2, '0');
    const yyyymmdd = `${year}-${month}-${day}`;
    return yyyymmdd;
}

const formatTime = (dateString, utc = false) => {
    const date = utc ? new Date(dateString) : new Date(new Date(dateString + 'Z').toLocaleString('en-US'));
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const hhmm = `${hours}:${minutes}:${seconds}`;
    return hhmm;
}

function daysUntil(dateIso) {
    const due = new Date(dateIso + 'Z').getTime();
    const now = Date.now();
    const diff = due - now;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function stringToColor(str) {
    let hash = 0;
    if (!str) return "#777";
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    const c = (hash & 0x00ffffff).toString(16).toUpperCase();
    return "#" + "00000".substring(0, 6 - c.length) + c;
}

function initials(name = "?") {
    return name
        .split(/\\s+/)
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
}

function LogsList({ logs, compact }) {
    if (!logs?.length) {
        return (
            <Stack alignItems="center" py={2} gap={1}>
                <HistoryIcon />
                <Typography variant="body2" color="text.secondary">No logs yet</Typography>
            </Stack>
        );
    }
    return (
        <List dense={!!compact} sx={{ width: "100%" }}>
            {logs.map((log) => (
                <ListItem key={log.Id} alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemAvatar>
                        <Avatar sx={{ bgcolor: stringToColor(log.userName) }}>{initials(log.userName)}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                        primary={
                            <Stack direction="row" gap={1} alignItems="center">
                                <Chip size="small" color={logTypesMeta[log.type]?.color ?? 'info'} label={logTypesMeta[log.type]?.label ?? log.type} variant="filled" />
                                <Typography variant="body2" color="text.secondary">
                                    {formatDate(log.createdAt)}, {formatDate(log.createdAt, true)}
                                </Typography>
                            </Stack>
                        }
                        secondary={
                            <>
                                <Typography component="span" variant="body2" color="text.primary">
                                    {log.userName}
                                </Typography>{" "}
                                <Typography component="span" variant="body2">{log.message}</Typography>
                            </>
                        }
                    />
                </ListItem>
            ))}
        </List>
    );
}

function Comments({ comments, onAdd }) {
    const [value, setValue] = useState("");

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
                Comments
            </Typography>
            <List sx={{ width: "100%" }}>
                {(!comments || comments.length === 0) && (
                    <ListItem sx={{ px: 0 }}>
                        <ListItemText primary={<Typography color="text.secondary">No comments yet</Typography>} />
                    </ListItem>
                )}
                {comments && comments.map((c) => (
                    <ListItem key={c.id} alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemAvatar>
                            <Avatar sx={{ bgcolor: stringToColor(c.userName) }}>{initials(c.userName)}</Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={
                                <Stack direction="row" gap={1} alignItems="center">
                                    <Typography variant="body2" fontWeight={600}>
                                        {c.userName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {formatDate(c.createdAt)}, {formatDate(c.createdAt, true)}
                                    </Typography>
                                </Stack>
                            }
                            secondary={<Typography variant="body2">{c.comment}</Typography>}
                        />
                    </ListItem>
                ))}
            </List>

            <Stack direction={{ xs: "column", sm: "row" }} gap={1} mt={1}>
                <TextField
                    fullWidth
                    placeholder="Write a comment…"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    multiline
                    minRows={2}
                />
                <Button
                    variant="contained"
                    startIcon={<CommentIcon />}
                    onClick={() => {
                        if (!value.trim()) return;
                        onAdd && onAdd(value.trim());
                        setValue("");
                    }}
                >
                    Add
                </Button>
            </Stack>
        </Box>
    );
}

function TaskDetails({
    task,
    logs = [],
    comments = [],
    context = {},
    onChangeState,
    onAddComment,
    onEditTask,
}) {
    const [menuEl, setMenuEl] = useState(null);
    const menuOpen = Boolean(menuEl);
    const [CLtabView, setCLTabView] = useState(0);
    const [DCtabView, setDCTabView] = useState(0);
    const [canEdit, setCanEdit] = useState(false);
    const [taskEdit, setTaskEdit] = useState(task);
    const [searchedUsers, setSearchedUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState(task.assignedUserName || "");

    const handleCommentLogsChange = (_, newValue) => {
        setCLTabView(newValue);
    };
    const handleDescriptionContextChange = (_, newValue) => {
        setDCTabView(newValue);
    };
    const dueInDays = useMemo(() => daysUntil(task.dueDate), [task.dueDate]);
    const stateBadge = stateMeta[task.state] || { label: `State ${task.state}`, color: "default" };
    const priorityBadge = priorityMeta[task.priority] || { label: `P${task.priority}`, icon: <FlagIcon fontSize="small" /> };
    const overdue = task.dueDate && new Date(task.dueDate).getTime() < Date.now() && task.state !== 3;

    function a11yProps(name, index) {
        return {
            id: `${name}-tab-${index}`,
            'aria-controls': `${name}-tabpanel-${index}`,
        };
    }

    function formatJson(json) {
        try {
            return JSON.stringify(JSON.parse(json), null, 4);
        } catch {
            return json;
        }
    }

    const searchUsers = async () => {
        try {
            const res = await fetch(`/api/security/users-search?query=${searchQuery}`, {
                method: 'GET',
                credentials: 'include',
            });
            if (!res.ok) {
                throw new Error(`Error: ${res.status} ${res.statusText}`);
            }
            const data = await res.json();
            setSearchedUsers(data);
        } catch (error) {
            console.error('Error fetching users:', error);
            setSearchedUsers([]);
        }
    }

    useEffect(() => {
        setTaskEdit(task);
        setSearchQuery(task.assignedUserName || "");
        setSearchedUsers([]);
        setCanEdit(false);
    }, [task]);

    const handleDownload = async (step, type = 'output') => {
        const res = await fetch(`/api/workflowadmin/stepFile/${step.id}/${type}`);

        if (!res.ok) {
            // handle 401/404/etc
            return;
        }

        const blob = await res.blob();
        // optional: use filename from Content-Disposition if your API sets it
        const fileName = type === 'output' ? step.output : step.request;

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }

    const getFileExtension = (filename) => {
        if (typeof filename !== "string") return "";
        const parts = filename.split(".");
        if (parts.length < 2) return "";
        return parts.pop().toLowerCase();
    }

    const handleSearchUsers = (e) => {
        setSearchQuery(e.target.value);
        setTaskEdit((prev) => ({
            ...prev,
            assignedUser: searchedUsers.find(user => user.email === e.target.value)?.id || null,
            assignedUserName: searchedUsers.find(user => user.email === e.target.value)?.email || null,
        }));
    }

    useEffect(() => {
        const id = setTimeout(() => {
            if (searchQuery && searchQuery.trim() !== "" && searchQuery.length >= 3) {
                searchUsers();
            } else {
                setSearchedUsers([]);
            }
        }, 300);
        return () => clearTimeout(id);
    }, [searchQuery]);

    return (
        <Box sx={{ width: '100%', mx: "auto" }}>
            <CardHeader
                action={
                    <>
                        {canEdit && (
                            <>
                                <Tooltip title="Save Changes">
                                    <IconButton color="primary" onClick={() => {
                                        onEditTask && onEditTask(taskEdit);
                                        setCanEdit(false);
                                    }}>
                                        <SaveIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancel Edit">
                                    <IconButton color="primary" onClick={() => {
                                        setTaskEdit(task);
                                        setCanEdit(false);
                                    }}>
                                        <CloseIcon />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                        <Tooltip title="More">
                            <IconButton onClick={(e) => setMenuEl(e.currentTarget)}>
                                <MoreVertIcon />
                            </IconButton>
                        </Tooltip>
                        <Menu anchorEl={menuEl} open={menuOpen} onClose={() => setMenuEl(null)}>
                            <MenuItem onClick={() => {
                                onChangeState && onChangeState(2);
                                setMenuEl(null);
                            }}>
                                <AssignmentTurnedInIcon fontSize="small" style={{ marginRight: 8 }} /> Mark as Done
                            </MenuItem>
                            {task.canEdit && (
                                <MenuItem onClick={() => {
                                    if (canEdit) {
                                        setTaskEdit(task);
                                    }
                                    setCanEdit(!canEdit);
                                    setMenuEl(null);
                                }}>
                                    <EditIcon fontSize="small" style={{ marginRight: 8 }} /> {canEdit ? "Cancel Edit" : "Edit Name"}
                                </MenuItem>
                            )}
                        </Menu>
                    </>
                }
                title={
                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                        {canEdit ? (
                            <FormControl size="small">
                                <TextField
                                    value={taskEdit.name}
                                    onChange={(e) => setTaskEdit({ ...taskEdit, name: e.target.value })}
                                    autoFocus
                                    size="small"
                                    variant="standard"
                                    sx={{ minWidth: '200px' }}
                                />
                            </FormControl>
                        ) : (
                            <Typography variant="h5">{task.name}</Typography>
                        )}
                        {task.systemTask && <Chip size="small" label="System" color="secondary" />}
                        <Chip size="small" label={stateBadge.label} color={stateBadge.color} />
                        {canEdit ? (
                            <FormControl size="small">
                                <Select
                                    value={taskEdit.priority}
                                    onChange={(e) => setTaskEdit({ ...taskEdit, priority: parseInt(e.target.value) })}
                                    inputProps={{
                                        name: 'priority',
                                        id: 'priority-select',
                                    }}
                                    variant="standard"
                                    sx={{ minWidth: '100px' }}
                                >
                                    {Object.entries(priorityMeta).map(([key, meta]) => (
                                        <MenuItem key={key} value={key}>
                                            {meta.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Chip size="small" icon={priorityBadge.icon} label={`Priority: ${priorityBadge.label}`} variant="outlined" />
                        )}
                    </Stack>
                }
                subheader={
                    <>
                        <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2" color="text.secondary">
                                ID: <MUILink underline="hover">{task.id}</MUILink>
                            </Typography>
                        </Stack>
                        <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2" color="text.secondary">
                                Created {formatDate(task.createdAt)}, {formatDate(task.createdAt, true)} by {task.createdByName || task.createdBy}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Updated {formatDate(task.updatedAt)}, {formatDate(task.updatedAt, true)} by {task.updatedByName || task.updatedBy}
                            </Typography>
                        </Stack>
                        <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                            <Stack direction="row" gap={0.5} alignItems="center">
                                <ScheduleIcon fontSize="small" />
                                <Typography variant="body2">
                                    Due {formatDate(task.dueDate)}
                                    {overdue ? " · OVERDUE" : dueInDays >= 0 ? ` · in ${dueInDays} day${dueInDays === 1 ? "" : "s"}` : ""}
                                </Typography>
                            </Stack>
                        </Stack>
                    </>
                }
            />

            {overdue && <LinearProgress color="error" />}

            <CardContent>

                <Stack direction={{ xs: "column", sm: "row" }} gap={1} mb={2} flexWrap="wrap">
                    {task.state === 1 && (
                        <Button variant="outlined" size="small" startIcon={<AssignmentTurnedInIcon />} sx={{ alignSelf: 'end' }} onClick={() => onChangeState && onChangeState(2)}>
                            Mark as Done
                        </Button>
                    )}
                    {[3, 2].includes(task.state) && (
                        <Button variant="outlined" size="small" startIcon={<HistoryIcon />} onClick={() => onChangeState && onChangeState(0)}>
                            Reopen
                        </Button>
                    )}
                    {task.state === 0 && (
                        <Button variant="outlined" size="small" startIcon={<KeyboardArrowRightIcon />} onClick={() => onChangeState && onChangeState(1)}>
                            Start
                        </Button>
                    )}
                    {![2, 3].includes(task.state) && (
                        <Button variant="outlined" size="small" color="error" startIcon={<CloseIcon />} onClick={() => onChangeState && onChangeState(3)}>
                            Cancel Task
                        </Button>
                    )}
                </Stack>

                {task.assignedUserName ? (
                    <Typography variant="body2" color="text.secondary">
                        Assigned to
                        {canEdit ? (
                            <FormControl size="small" sx={{ ml: 1, minWidth: '200px' }}>
                                <AutoCompleteComponent
                                    id="search-users"
                                    dataSource={searchedUsers}
                                    fields={{
                                        value: 'email'
                                    }}
                                    value={searchQuery}
                                    debounceDelay={300}
                                    popupHeight="200px"
                                    onChange={handleSearchUsers}
                                    placeholder="john@email.com" />
                            </FormControl>

                        ) : (<strong> {task.assignedUserName}</strong>)}
                    </Typography>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Unassigned
                    </Typography>
                )}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={DCtabView} onChange={handleDescriptionContextChange} aria-label="description-context tabs">
                        <Tab label="Description" {...a11yProps('descriptioncontext', 0)} />
                        <Tab label="Context" {...a11yProps('descriptioncontext', 1)} />
                    </Tabs>
                </Box>
                <div
                    role="tabpanel"
                    hidden={DCtabView !== 0}
                    {...a11yProps('descriptioncontext', 0)}
                >
                    {DCtabView === 0 && (
                        <>
                            {canEdit ? (
                                <FormControl fullWidth>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        maxRows={10}
                                        value={taskEdit.description || ""}
                                        onChange={(e) => setTaskEdit({ ...taskEdit, description: e.target.value })}
                                        variant="outlined"
                                        sx={{ mb: 2 }}
                                    />
                                </FormControl>
                            ) : (
                                task.description ? (
                                    <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre' }}>{task.description}</Typography>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>No description</Typography>
                                )
                            )}
                        </>
                    )}
                </div>
                <div
                    role="tabpanel"
                    hidden={DCtabView !== 1}
                    {...a11yProps('descriptioncontext', 1)}
                >
                    {DCtabView === 1 && (
                        context && (
                            <>
                                <Typography variant="h6" sx={{ mb: 1 }}>{context.key}</Typography>
                                {context.lastAttemptStatus > 0 && (
                                    <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                                        <Typography variant="body2" color="text.secondary">
                                            Last Attempt:
                                        </Typography>
                                        <Badge width="10rem" type={WORKFLOW_STATUS_COLOR[context.lastAttemptStatus]} icon={WORKFLOW_STATUS_ICON[context.lastAttemptStatus]}>
                                            {WORKFLOW_STATUS[context.lastAttemptStatus]}
                                        </Badge>
                                        <br />
                                    </Stack>
                                )}
                                <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                                    <Typography variant="body2" color="text.secondary">
                                        Status:
                                    </Typography>
                                    <Badge width="10rem" type={WORKFLOW_STATUS_COLOR[context.contextStatus]} icon={WORKFLOW_STATUS_ICON[context.contextStatus]}>
                                        {WORKFLOW_STATUS[context.contextStatus]}
                                    </Badge>
                                </Stack>
                                <hr />

                                <Typography variant="h6" sx={{ mb: 1 }}>Logs</Typography>
                                {context.logs.map((log) => (
                                    <>
                                        <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                            <Badge type={WORKFLOW_LOG_TYPE_COLOR[log.type]} icon={WORKFLOW_LOG_TYPE_ICON[log.type]} width='150px'>
                                                {WORKFLOW_LOG_TYPES[log.type]}
                                            </Badge>
                                            <Badge type='default' width='150px' icon={<ScheduleIcon sx={{ fontSize: DEFAULT_ICON_SIZE }} />}>
                                                {`${formatDateLog(log.createdAt)} ${formatTime(log.createdAt)}`}
                                            </Badge>
                                        </Box>
                                        {WORKFLOW_LOG_TYPES[log.type] != 'DOCUMENT' && (
                                            <Box sx={{ width: '100%' }}>
                                                <pre style={{
                                                    maxHeight: '400px',
                                                    maxWidth: '100%',
                                                    overflow: 'auto',
                                                    whiteSpace: 'pre-wrap'
                                                }}>
                                                    {formatLog(log.log, log.dataType)}
                                                </pre>
                                            </Box>
                                        )}
                                    </>
                                ))}
                                <Box>
                                    <Typography variant="h6">Payload</Typography>
                                    <pre style={{
                                        border: '1px solid #ccc',
                                        height: '250px',
                                        overflow: 'auto',
                                    }}>{formatJson(context.contextPayload, null, 4)}</pre>
                                </Box>

                                <Box width='100%'>
                                    <Grid2 container columns={12} spacing={1}>
                                        <Grid2 size={6}>
                                            <Typography variant="h6">Request</Typography>
                                            {context.requestType == WorkflowDetailDataType.Text && (
                                                <>
                                                    {context.request}
                                                </>
                                            )}
                                            {context.requestType == WorkflowDetailDataType.Json && (
                                                <pre style={{
                                                    border: '1px solid #ccc',
                                                    height: '250px',
                                                    overflow: 'auto',
                                                }}>{formatLog(context.request, 1)}</pre>
                                            )}
                                            {context.requestType == WorkflowDetailDataType.Document && (
                                                <Box>
                                                    <FileRow name={context.request} ext={getFileExtension(context.request)} onDownload={() => handleDownload(context, 'request')} />
                                                </Box>
                                            )}
                                        </Grid2>
                                        <Grid2 size={6}>
                                            <Typography variant="h6">Output</Typography>
                                            {context.outputType == WorkflowDetailDataType.Text && (
                                                <>
                                                    {context.output}
                                                </>
                                            )}
                                            {context.outputType == WorkflowDetailDataType.Json && (
                                                <pre style={{
                                                    border: '1px solid #ccc',
                                                    height: '250px',
                                                    overflow: 'auto',
                                                }}>{formatLog(context.output, 1)}</pre>
                                            )}
                                            {context.outputType == WorkflowDetailDataType.Document && (
                                                <Box>
                                                    <FileRow name={context.output} ext={getFileExtension(context.output)} onDownload={() => handleDownload(context)} />
                                                </Box>
                                            )}
                                        </Grid2>
                                    </Grid2>
                                </Box>
                            </>
                        )
                    )}
                </div>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={CLtabView} onChange={handleCommentLogsChange} aria-label="basic tabs example">
                        <Tab label="Comments" {...a11yProps('commentlogs', 0)} />
                        <Tab label="Logs" {...a11yProps('commentlogs', 1)} />
                    </Tabs>
                </Box>
                <div
                    role="tabpanel"
                    hidden={CLtabView !== 0}
                    {...a11yProps('commentlogs', 0)}
                >
                    {CLtabView === 0 && (
                        <Comments comments={comments || []} onAdd={(msg) => onAddComment && onAddComment(msg)} />
                    )}
                </div>
                <div
                    role="tabpanel"
                    hidden={CLtabView !== 1}
                    {...a11yProps('commentlogs', 1)}
                >
                    {CLtabView === 1 && (
                        <Box>
                            <Typography variant="h6">Activity</Typography>
                            <LogsList logs={(logs || []).slice(0, 5)} compact />
                        </Box>
                    )}
                </div>
            </CardContent>

        </Box >
    );
}

export default TaskDetails;
